import { format, isToday, isYesterday } from "date-fns";
import { MessageSquare, Users } from "lucide-react";
import type { TextConversation } from "@/hooks/useTextMessages";

function formatDate(dateStr: string) {
  const date = new Date(dateStr);
  if (isToday(date)) return format(date, "h:mm a");
  if (isYesterday(date)) return "Yesterday";
  return format(date, "MMM d");
}

function displayTitle(conv: TextConversation): string {
  if (conv.chat_type === "group") {
    if (conv.chat_title && conv.chat_title.trim()) return conv.chat_title.trim();
    const participants = conv.participants || [];
    const named = participants
      .map((p) => p.name || p.phone)
      .filter((s): s is string => !!s);
    if (named.length === 0) return "Group";
    if (named.length <= 2) return named.join(" & ");
    return `${named[0]}, ${named[1]} + ${named.length - 2}`;
  }
  return conv.contact_name || conv.phone_number;
}

type Props = {
  conversation: TextConversation;
  onClick: () => void;
};

export function TextRow({ conversation, onClick }: Props) {
  const hasUnread = conversation.unread_count > 0;
  const isGroup = conversation.chat_type === "group";
  const title = displayTitle(conversation);
  const participantCount = conversation.participants?.length ?? 0;

  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-3 border-b border-border hover:bg-muted/50 transition-colors ${
        hasUnread ? "bg-muted/30" : ""
      }`}
    >
      <div className="flex items-start gap-2">
        <div className="pt-0.5 shrink-0">
          {isGroup ? (
            <Users className={`h-4 w-4 ${hasUnread ? "text-primary" : "text-muted-foreground"}`} />
          ) : (
            <MessageSquare className={`h-4 w-4 ${hasUnread ? "text-primary" : "text-muted-foreground"}`} />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className={`text-sm truncate ${hasUnread ? "font-semibold" : ""}`}>
              {title}
              {isGroup && participantCount > 0 && (
                <span className="ml-1.5 text-[10px] font-normal text-muted-foreground">
                  · {participantCount + 1}
                </span>
              )}
            </span>
            <span className="text-[10px] text-muted-foreground shrink-0">
              {formatDate(conversation.latest_date)}
            </span>
          </div>
          <p className={`text-xs truncate mt-0.5 ${hasUnread ? "text-foreground" : "text-muted-foreground"}`}>
            {conversation.latest_message}
          </p>
          {hasUnread && (
            <div className="flex items-center gap-1 mt-1">
              <span className="w-4 h-4 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center font-bold">
                {conversation.unread_count}
              </span>
            </div>
          )}
        </div>
      </div>
    </button>
  );
}
